import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage, useIntl } from '@techstyle/react-intl';

import { useByoContext } from '../ByoProvider';
import { Component as ProductImage } from '../ProductImage';
import { Component as ProductPlaceholderImage } from '../ProductPlaceholderImage';
import { Component as StaticIcon } from '../StaticIcon';

const DynamicFormattedMessage = FormattedMessage;

const imageRatio = 0.75;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledProductImage = styled(ProductImage)`
  margin: 4px;
  padding: 4px;
  background-color: white;
  width: ${props => props.imageWidth}px;
  height: ${props => props.imageWidth / imageRatio}px;
  box-shadow: 0px 2px 4px 0px ${({ theme }) => theme.colors.lightShadow1};
`;

const PlusIcon = styled(StaticIcon).attrs({
  type: 'plus',
  size: 15,
})`
  color: ${({ theme }) => theme.colors.promo};
`;

export default function ByoImageProgressBar({
  imageWidth = 90,
  isTextVisible = false,
}) {
  const { addedItems, selectedByoSet } = useByoContext();
  const intl = useIntl();

  const images = useMemo(() => {
    return selectedByoSet.config.map((config, index) => {
      if (addedItems[index]) {
        return (
          <StyledProductImage
            key={index}
            data-autotag={`item_box_${index + 1}`}
            imageWidth={imageWidth}
            product={addedItems[index]}
            angle="LAYDOWN"
            size={6}
            alt={intl.formatMessage(
              {
                id: 'site_pdp.image_progress_bar',
                defaultMessage: 'Item number {index} in set',
              },
              { index: index + 1 }
            )}
          />
        );
      }

      return (
        <ProductPlaceholderImage
          key={index}
          data-autotag={`item_box_${index + 1}`}
          width={imageWidth}
          height={imageWidth / imageRatio}
        >
          {isTextVisible ? (
            <DynamicFormattedMessage
              id={config.placeholderImageRb.id}
              defaultMessage={config.placeholderImageRb.defaultMessage}
            />
          ) : null}
        </ProductPlaceholderImage>
      );
    });
  }, [imageWidth, intl, isTextVisible, selectedByoSet, addedItems]);

  const imagesWithPlusSign = (() => {
    let hasPlusSign = false;
    return images.reduce(
      (accum, _next, index, arr) => {
        if (!addedItems[index] && !hasPlusSign) {
          hasPlusSign = true;
          const imagesCopy = [...arr];
          imagesCopy.splice(index, 0, <PlusIcon />);
          return imagesCopy;
        }
        return accum;
      },
      [...images]
    );
  })();

  return <Wrapper>{imagesWithPlusSign}</Wrapper>;
}

ByoImageProgressBar.propTypes = {
  imageWidth: PropTypes.number,
  isTextVisible: PropTypes.bool,
};
